import React, { useState, useEffect, useCallback } from 'react';
import { theme } from '../../styles/theme';

interface TutorialScreenProps {
  onBack: () => void;
  /** When true, renders as an overlay with backdrop instead of full-screen */
  overlay?: boolean;
}

// ─── SVG Diagram Helpers ───────────────────────────────

/** Vision cone diagram — player dot + directional cone arc */
function VisionConeDiagram({ color = '#44aaff', label = 'YOU' }: { color?: string; label?: string }) {
  return (
    <svg width="180" height="120" viewBox="0 0 180 120" style={{ display: 'block', margin: '12px auto' }}>
      {/* Cone */}
      <path d="M 40 90 L 140 30 L 160 70 Z" fill={color} fillOpacity="0.15" stroke={color} strokeWidth="1.5" strokeOpacity="0.5" />
      {/* Player dot */}
      <circle cx="40" cy="90" r="8" fill={color} />
      <text x="40" y="115" textAnchor="middle" fill={color} fontSize="10" fontFamily="monospace" fontWeight="700">{label}</text>
      {/* Range label */}
      <text x="120" y="20" textAnchor="middle" fill={theme.colors.textDim} fontSize="9" fontFamily="monospace">detection range</text>
    </svg>
  );
}

/** Signal circle diagram — offset center + radius ring */
function SignalDiagram() {
  return (
    <svg width="200" height="140" viewBox="0 0 200 140" style={{ display: 'block', margin: '12px auto' }}>
      {/* Outer ring */}
      <circle cx="100" cy="70" r="50" fill="rgba(255, 51, 68, 0.08)" stroke="#ff3344" strokeWidth="1.5" strokeDasharray="6 4" strokeOpacity="0.5" />
      {/* Center dot (offset — displayed position) */}
      <circle cx="100" cy="70" r="5" fill="#ff3344" fillOpacity="0.6" />
      {/* Actual position (hidden from player, shown dimmed for tutorial) */}
      <circle cx="115" cy="60" r="3" fill="#ff3344" fillOpacity="0.25" strokeDasharray="2 2" stroke="#ff3344" strokeWidth="1" />
      <line x1="100" y1="70" x2="115" y2="60" stroke="#ff3344" strokeWidth="0.5" strokeDasharray="2 2" strokeOpacity="0.3" />
      <text x="100" y="130" textAnchor="middle" fill="#ff3344" fontSize="9" fontFamily="monospace" fontWeight="600">AGENT SIGNAL</text>
      <text x="100" y="14" textAnchor="middle" fill={theme.colors.textDim} fontSize="8" fontFamily="monospace">agent is somewhere in this area</text>
    </svg>
  );
}

/** Zone color legend */
function ZoneLegend() {
  const zones = [
    { name: 'Commercial', color: '#ff4466' },
    { name: 'Residential', color: '#44bbcc' },
    { name: 'Park', color: '#22cc55' },
    { name: 'Transit', color: '#ff8844' },
    { name: 'Civic', color: '#4488ff' },
    { name: 'Mixed-Use', color: '#cc66ff' },
  ];
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', justifyContent: 'center', margin: '12px 0' }}>
      {zones.map(z => (
        <div key={z.name} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '12px', height: '12px', borderRadius: '2px', background: z.color, opacity: 0.7 }} />
          <span style={{ fontSize: '10px', color: z.color, fontFamily: theme.fonts.mono, fontWeight: 600 }}>{z.name}</span>
        </div>
      ))}
    </div>
  );
}

/** Combat scenarios diagram */
function CombatDiagram() {
  return (
    <svg width="260" height="100" viewBox="0 0 260 100" style={{ display: 'block', margin: '12px auto' }}>
      {/* Ambush */}
      <circle cx="30" cy="40" r="10" fill="#44aaff" />
      <path d="M 30 40 L 70 20 L 80 45 Z" fill="#44aaff" fillOpacity="0.15" stroke="#44aaff" strokeWidth="1" />
      <circle cx="65" cy="35" r="8" fill="#ff3344" />
      <text x="50" y="80" textAnchor="middle" fill="#44ff44" fontSize="9" fontFamily="monospace" fontWeight="700">AMBUSH</text>
      <text x="50" y="92" textAnchor="middle" fill={theme.colors.textDim} fontSize="7" fontFamily="monospace">you see them</text>
      {/* Confrontation */}
      <circle cx="120" cy="40" r="10" fill="#44aaff" />
      <circle cx="155" cy="40" r="8" fill="#ff3344" />
      <line x1="132" y1="40" x2="145" y2="40" stroke="#ffaa44" strokeWidth="2" strokeDasharray="3 2" />
      <text x="137" y="80" textAnchor="middle" fill="#ffaa44" fontSize="9" fontFamily="monospace" fontWeight="700">CONFRONTATION</text>
      <text x="137" y="92" textAnchor="middle" fill={theme.colors.textDim} fontSize="7" fontFamily="monospace">both see each other</text>
      {/* Ambushed */}
      <circle cx="220" cy="40" r="10" fill="#44aaff" />
      <circle cx="240" cy="25" r="8" fill="#ff3344" />
      <path d="M 240 25 L 210 35 L 215 50 Z" fill="#ff3344" fillOpacity="0.15" stroke="#ff3344" strokeWidth="1" />
      <text x="230" y="80" textAnchor="middle" fill="#ff3030" fontSize="9" fontFamily="monospace" fontWeight="700">AMBUSHED</text>
      <text x="230" y="92" textAnchor="middle" fill={theme.colors.textDim} fontSize="7" fontFamily="monospace">they see you</text>
    </svg>
  );
}

/** Pickup types legend */
function PickupLegend() {
  const types = [
    { label: 'W', name: 'Weapon Cache', color: '#ff5050' },
    { label: 'G', name: 'Gadget Stash', color: '#44cc66' },
    { label: '?', name: 'Supply Drop', color: '#ffdd44' },
  ];
  return (
    <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', margin: '12px 0' }}>
      {types.map(t => (
        <div key={t.label} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '24px', height: '24px', borderRadius: '50%', background: t.color,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '12px', fontWeight: 700, fontFamily: theme.fonts.mono, color: theme.colors.background,
          }}>{t.label}</div>
          <span style={{ fontSize: '10px', color: t.color, fontFamily: theme.fonts.mono }}>{t.name}</span>
        </div>
      ))}
    </div>
  );
}

/** Hotkey badge */
function Key({ children }: { children: string }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', background: 'rgba(255,255,255,0.1)',
      border: '1px solid rgba(255,255,255,0.2)', borderRadius: '3px',
      fontFamily: theme.fonts.mono, fontSize: '12px', fontWeight: 700, color: theme.colors.textPrimary,
      margin: '0 2px',
    }}>{children}</span>
  );
}

// ─── Slide Definitions ─────────────────────────────────

interface Slide {
  title: string;
  content: React.ReactNode;
}

function buildSlides(): Slide[] {
  const text: React.CSSProperties = { fontSize: '12px', color: theme.colors.textSecondary, lineHeight: '1.8', marginTop: '8px' };
  const highlight: React.CSSProperties = { color: theme.colors.textPrimary, fontWeight: 600 };
  const accent: React.CSSProperties = { color: theme.colors.accentGreen, fontWeight: 600 };

  return [
    // 1. Welcome
    {
      title: 'BRIEFING',
      content: (
        <div style={text}>
          <p>Your cover is blown. The agency has deployed a <span style={{ color: '#ff3344', fontWeight: 600 }}>sleeper agent</span> to hunt you down in a real city.</p>
          <p>Your mission: <span style={accent}>navigate the streets, gather intel, and either eliminate the agent or extract safely.</span></p>
          <p>Every round is a cat-and-mouse hunt on a real map. The streets, buildings, and transit lines are all from OpenStreetMap data.</p>
        </div>
      ),
    },
    // 2. The Map
    {
      title: 'THE MAP',
      content: (
        <div style={text}>
          <p>The city is divided into <span style={highlight}>zones</span> that affect gameplay. Press <Key>Q</Key> to toggle zone colors:</p>
          <ZoneLegend />
          <p><span style={{ color: '#ff4466', fontWeight: 600 }}>Commercial zones</span> generate the strongest signal — moving through busy streets is loud. <span style={{ color: '#22cc55', fontWeight: 600 }}>Parks</span> and <span style={{ color: '#44bbcc', fontWeight: 600 }}>residential</span> areas are quieter.</p>
          <p>Press <Key>R</Key> to see road classifications — major roads, minor roads, residential streets, and footpaths each affect signal differently.</p>
        </div>
      ),
    },
    // 3. Movement & AP
    {
      title: 'MOVEMENT & ACTION POINTS',
      content: (
        <div style={text}>
          <p>Each turn you have <span style={accent}>Action Points (AP)</span> to spend on cards. Movement cards let you click the map to walk along real streets.</p>
          <p>Your <span style={highlight}>hand</span> of cards is at the bottom of the screen. Click a movement card, then click where you want to go. The path follows real roads.</p>
          <p>When you're done, press <Key>Enter</Key> to end your turn. The agent then takes their turn.</p>
          <p style={{ fontSize: '10px', color: theme.colors.textDim, marginTop: '12px' }}>Tip: You can play multiple cards per turn as long as you have AP. Plan your moves before committing.</p>
        </div>
      ),
    },
    // 4. Signal System
    {
      title: 'SIGNAL',
      content: (
        <div style={text}>
          <p>Every time you move, you generate a <span style={{ color: '#44aaff', fontWeight: 600 }}>signal</span> based on the terrain you crossed. The agent reads your signals to track you down.</p>
          <SignalDiagram />
          <p>Signals appear as circles on the map. <span style={highlight}>Stronger signals</span> have smaller circles (more precise). The agent's AI uses your signals to build a belief about where you are.</p>
          <p>Moving through <span style={{ color: '#ff4466' }}>commercial zones</span> creates loud signal. <span style={{ color: '#22cc55' }}>Parks</span> and footpaths are much quieter.</p>
        </div>
      ),
    },
    // 5. Vision Cones
    {
      title: 'VISION CONES',
      content: (
        <div style={text}>
          <p>Both you and the agent have a <span style={highlight}>directional vision cone</span>. You can only detect each other when one is inside the other's cone.</p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '20px' }}>
            <VisionConeDiagram color="#44aaff" label="YOU" />
            <VisionConeDiagram color="#ff3344" label="AGENT" />
          </div>
          <p>Drag on the map to <span style={accent}>aim your vision cone</span>. After each move, your cone rotates to face your direction of travel.</p>
          <p style={{ fontSize: '10px', color: theme.colors.textDim, marginTop: '8px' }}>The agent's cone is hidden unless you have intel revealing it (Cold Opening passive, enemy spotted, etc.).</p>
        </div>
      ),
    },
    // 6. Intel Cards
    {
      title: 'INTEL CARDS',
      content: (
        <div style={text}>
          <p>Intel cards help you find the agent without getting close:</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', margin: '10px 0' }}>
            {[
              { name: 'Sweep', desc: 'Scan an area — reveals if the agent is inside', color: '#00ff88' },
              { name: 'Informant', desc: 'Contact at a nearby POI tips you off', color: '#00ff88' },
              { name: 'Spotter', desc: 'Place a detection zone on the map', color: '#00ff88' },
              { name: 'Wire Tap', desc: 'Tap a street — triggers if the agent crosses', color: '#00ff88' },
              { name: 'Traffic Analysis', desc: 'Reveals which road the agent is on', color: '#00ff88' },
              { name: 'Stakeout', desc: 'Place a vision cone at a building', color: '#00ff88' },
            ].map(c => (
              <div key={c.name} style={{ fontSize: '10px', lineHeight: '1.5' }}>
                <span style={{ color: c.color, fontWeight: 700 }}>{c.name}</span>
                <span style={{ color: theme.colors.textDim }}> — {c.desc}</span>
              </div>
            ))}
          </div>
          <p>Press <Key>F</Key> to snap the map to your latest intel.</p>
        </div>
      ),
    },
    // 7. Agent Signals
    {
      title: 'READING THE AGENT',
      content: (
        <div style={text}>
          <p>The agent generates <span style={{ color: '#ff3344', fontWeight: 600 }}>red signals</span> as they move — each one a clue about where they are.</p>
          <p style={{
            background: 'rgba(255, 100, 100, 0.08)',
            border: '1px solid rgba(255, 100, 100, 0.3)',
            borderRadius: '4px',
            padding: '8px 12px',
            fontSize: '11px',
            color: theme.colors.textPrimary,
            margin: '10px 0',
          }}>
            <span style={{ color: '#ff6464', fontWeight: 700 }}>⚠ You are blind by default.</span> Agent passive signals do NOT show on your map automatically. To see them you need an active <span style={highlight}>card</span>, <span style={highlight}>passive</span>, or <span style={highlight}>item</span>:
            <br />
            <span style={{ fontSize: '10px', color: theme.colors.textDim, lineHeight: '1.6' }}>
              · <strong>Counter-Surveillance</strong> (card) — reveals last turn's agent passives
              <br />
              · <strong>Radio Scanner</strong> (item) — reveals agent passive signals while in inventory (+25% strength)
              <br />
              · <strong>Echo Chamber / Blown Entrance</strong> (passives) — always-on or early-round bypass
            </span>
          </p>
          <p>When you do see them: <span style={highlight}>strong signals</span> (small circles) = loud zone. <span style={highlight}>Weak signals</span> (large circles) = quieter, agent could be anywhere inside.</p>
          <p>Signals <span style={highlight}>fade after 3 turns</span>. Intel cards (Sweep, Informant, Spotter, etc.) work regardless of blindness — they give you fresh, direct reads.</p>
          <p>The agent reads <span style={{ color: '#44aaff' }}>your signals</span> freely — so every move is a tradeoff between progress and stealth.</p>
        </div>
      ),
    },
    // 8. Items & Pickups
    {
      title: 'ITEMS & PICKUPS',
      content: (
        <div style={text}>
          <p>Colored dots on the map are <span style={highlight}>item pickups</span>. Walk near one and a Scavenge card appears in your hand:</p>
          <PickupLegend />
          <p>Your inventory holds <span style={accent}>4 items</span>. Weapons are used in combat. Gadgets provide tactical options. Items can also be <span style={highlight}>extracted for Marks</span> (currency) at the end of a round.</p>
          <p style={{ fontSize: '10px', color: theme.colors.textDim, marginTop: '8px' }}>Hover over a pickup dot to see the loot table — which items are available and their drop chances.</p>
        </div>
      ),
    },
    // 9. Combat
    {
      title: 'COMBAT',
      content: (
        <div style={text}>
          <p>Combat triggers when vision cones overlap. Four scenarios, based on who saw whom first:</p>
          <CombatDiagram />
          <p style={{ fontSize: '10px', color: theme.colors.textDim, marginTop: '2px' }}>
            A fourth scenario — <span style={{ color: '#cc66ff', fontWeight: 700 }}>SNIPED</span> — fires when the agent is on a rooftop with a sniper rifle and has line of sight to you on the ground.
          </p>
        </div>
      ),
    },
    // 10. Combat matrix — full outcome table
    // 10. Combat matrix — offense side (you are the attacker)
    {
      title: 'COMBAT MATRIX — OFFENSE',
      content: (() => {
        // Shared matrix styles used across both matrix slides.
        const section: React.CSSProperties = {
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '6px',
          fontFamily: theme.fonts.mono,
          fontSize: '9px',
          overflow: 'hidden',
          alignSelf: 'start',
        };
        const header: React.CSSProperties = {
          padding: '5px 9px',
          background: 'rgba(255,255,255,0.05)',
          letterSpacing: '1.5px',
          fontSize: '9px',
          fontWeight: 700,
        };
        const rowStyle: React.CSSProperties = {
          padding: '4px 9px',
          borderTop: '1px solid rgba(255,255,255,0.05)',
          lineHeight: '1.4',
          fontSize: '9px',
        };
        const kill = { color: '#44ff44', fontWeight: 700 } as const;
        const die = { color: '#ff3030', fontWeight: 700 } as const;
        const escape = { color: '#ffaa44', fontWeight: 700 } as const;
        const roll = { color: '#66ccff', fontWeight: 700 } as const;

        // One row. Condition is bolded and flows into the outcome via em-dash
        // so rows wrap naturally at narrow widths — works better in a side-
        // by-side 2-column layout than a fixed grid.
        const r = (cond: React.ReactNode, outcome: React.ReactNode) => (
          <div style={rowStyle}>
            <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>{cond}</span>
            <span style={{ color: theme.colors.textSecondary }}> — {outcome}</span>
          </div>
        );

        return (
          <div style={{ ...text, marginTop: '6px' }}>
            <p style={{ fontSize: '11px', color: theme.colors.textDim }}>
              Scenarios where <span style={highlight}>you attack first</span>. Weapon wins outright, rolls settle the rest.
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '8px',
              marginTop: '8px',
            }}>
              <div style={section}>
                <div style={{ ...header, color: '#44ff44' }}>AMBUSH</div>
                {r('Pistol / Knife', <><span style={kill}>INSTANT KILL</span></>)}
                {r('Sniper (rooftop only)', <><span style={kill}>INSTANT KILL</span>. Sniper on ground can't be used here.</>)}
                {r('Improvised Strike · agent unarmed', <><span style={roll}>ROLL</span>: your prep + 20% vs agent prep base. Winner kills.</>)}
                {r('Improvised Strike · agent armed', <><span style={roll}>ROLL</span>: your prep + 20% vs agent prep + <strong>50% weapon edge</strong>.</>)}
              </div>

              <div style={section}>
                <div style={{ ...header, color: '#ffaa44' }}>CONFRONTATION</div>
                {r('Flashbang', <><span style={escape}>ESCAPE</span> — item consumed.</>)}
                {r('Your gun · agent unarmed/knife', <><span style={kill}>INSTANT KILL</span> (agent flashbang/vest can escape).</>)}
                {r('Gun vs gun', <><span style={roll}>PREP DRAW</span> (you) vs 50% (agent). Winner kills; vest may save from losing pistol shot.</>)}
                {r('Knife/unarmed · agent gun', <><span style={die}>DIE</span>. Vest saves → vest exhausted.</>)}
                {r('Melee / unarmed both sides', <><span style={roll}>ROLL</span>: prep vs agent base. Winner kills.</>)}
                {r('Improvised Strike', <>70% of your prep % to land. Hit: <span style={kill}>KILL</span>. Miss: agent retaliates.</>)}
              </div>
            </div>

            <p style={{ fontSize: '10px', color: theme.colors.textDim, marginTop: '10px', lineHeight: '1.5' }}>
              <span style={highlight}>Rolls don't tie</span> — combat re-rolls until one side succeeds and the other fails. "Survive" outcomes come only from items.
            </p>
            <p style={{ fontSize: '10px', color: theme.colors.textDim, marginTop: '4px', lineHeight: '1.5' }}>
              Continue to the next slide for <span style={highlight}>defense scenarios</span> (AMBUSHED / SNIPED) and the item cheat sheet.
            </p>
          </div>
        );
      })(),
    },
    // 11. Combat matrix — defense side (you are attacked)
    {
      title: 'COMBAT MATRIX — DEFENSE',
      content: (() => {
        const section: React.CSSProperties = {
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '6px',
          fontFamily: theme.fonts.mono,
          fontSize: '9px',
          overflow: 'hidden',
          alignSelf: 'start',
        };
        const header: React.CSSProperties = {
          padding: '5px 9px',
          background: 'rgba(255,255,255,0.05)',
          letterSpacing: '1.5px',
          fontSize: '9px',
          fontWeight: 700,
        };
        const rowStyle: React.CSSProperties = {
          padding: '4px 9px',
          borderTop: '1px solid rgba(255,255,255,0.05)',
          lineHeight: '1.4',
          fontSize: '9px',
        };
        // Note: no KILL color alias here because the defense-side outcomes
        // never show an instant-kill cell (winning a roll still shows the
        // ROLL tag with "winner kills" described inline, not a green KILL).
        const die = { color: '#ff3030', fontWeight: 700 } as const;
        const escape = { color: '#ffaa44', fontWeight: 700 } as const;
        const saved = { color: '#ffdd44', fontWeight: 700 } as const;
        const roll = { color: '#66ccff', fontWeight: 700 } as const;

        const r = (cond: React.ReactNode, outcome: React.ReactNode) => (
          <div style={rowStyle}>
            <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>{cond}</span>
            <span style={{ color: theme.colors.textSecondary }}> — {outcome}</span>
          </div>
        );

        return (
          <div style={{ ...text, marginTop: '6px' }}>
            <p style={{ fontSize: '11px', color: theme.colors.textDim }}>
              Scenarios where <span style={highlight}>the agent attacks first</span>. Items are the only way to skip the roll; otherwise winner kills, loser dies.
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '8px',
              marginTop: '8px',
            }}>
              <div style={section}>
                <div style={{ ...header, color: '#ff3030' }}>AMBUSHED</div>
                {r('Flashbang', <><span style={escape}>ESCAPE</span> — item consumed.</>)}
                {r('Hard Target passive', <><span style={escape}>ESCAPE</span> — free, 1×/round.</>)}
                {r('Agent knife', <><span style={die}>DIE</span>. Vest does NOT help vs blade.</>)}
                {r('Agent gun · vest', <><span style={saved}>SAVED</span> — vest absorbed the shot.</>)}
                {r('Agent gun · no vest', <><span style={die}>DIE</span>.</>)}
                {r('Agent unarmed · you armed', <><span style={roll}>ROLL</span>: your prep + <strong>50% weapon edge</strong> vs agent prep.</>)}
                {r('Both unarmed', <><span style={roll}>ROLL</span>: your prep vs agent base.</>)}
                {r('Defenseless', <><span style={die}>DIE</span> (no prep + no weapon + no items).</>)}
              </div>

              <div style={section}>
                <div style={{ ...header, color: '#cc66ff' }}>SNIPED</div>
                {r('Hard Target passive', <><span style={escape}>ESCAPE</span> — free, 1×/round.</>)}
                {r('Vest', <><span style={saved}>SAVED</span> — round absorbed.</>)}
                {r('Within 50m of building', <><span style={escape}>ESCAPE</span> — duck inside.</>)}
                {r('Otherwise', <><span style={die}>DIE</span>. Flashbang useless at range.</>)}
              </div>
            </div>

            {/* Items cheat sheet — full-width below the two columns */}
            <div style={{ ...section, marginTop: '8px' }}>
              <div style={{ ...header, color: '#66ccff' }}>ITEM CHEAT SHEET</div>
              {r('Flashbang', <>Auto-escape on Confrontation / Ambushed. Useless vs Sniped.</>)}
              {r('Bulletproof vest', <>Absorbs one pistol/sniper round. Useless vs knife. Consumed on use.</>)}
              {r('Hard Target (passive)', <>Once per round, auto-escape from Ambushed or Sniped.</>)}
            </div>

            <p style={{ fontSize: '10px', color: theme.colors.textDim, marginTop: '10px', lineHeight: '1.5' }}>
              An encounter is never dismissable — if you have no viable weapon you'll see a <strong>FIGHT TO THE DEATH</strong> button. A bulletproof vest still gives you a chance.
            </p>
          </div>
        );
      })(),
    },
    // 10. Prep & Improvised Strike
    {
      title: 'PREP & IMPROVISED STRIKE',
      content: (
        <div style={text}>
          <p><span style={accent}>Prep</span> is a stacking combat bonus that builds over the round. It improves your odds in confrontations and unarmed ambushes.</p>
          <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '6px', padding: '10px', margin: '10px 0' }}>
            <div style={{ fontSize: '10px', color: theme.colors.textDim, letterSpacing: '1px', marginBottom: '6px' }}>PREP SOURCES</div>
            <div style={{ fontSize: '11px', lineHeight: '1.8' }}>
              <div><span style={{ color: '#ff5050' }}>Improvised Strike</span> — +10% per play (when no target visible)</div>
              <div><span style={{ color: '#ffaa44' }}>Instinct</span> — +5% (also gives 360° vision for 1 turn)</div>
              <div><span style={{ color: '#ffaa44' }}>Sharpen</span> — +10% for every card played that turn</div>
            </div>
          </div>
          <p>Prep caps at <span style={highlight}>90%</span> — it can never guarantee a win. The prep bar in the HUD shows your current bonus.</p>
        </div>
      ),
    },
    // 11. Extraction
    {
      title: 'EXTRACTION',
      content: (
        <div style={text}>
          <p>To escape the city, you need to <span style={accent}>extract</span>:</p>
          <div style={{ margin: '10px 0', lineHeight: '2' }}>
            <div>1. Collect <span style={highlight}>passphrases</span> from dead drops (blue diamonds on the map)</div>
            <div>2. Keep your <span style={{ color: '#ffaa44', fontWeight: 600 }}>exposure</span> below the threshold (shown in the HUD)</div>
            <div>3. Reach an <span style={accent}>extraction point</span> (green markers on the map edge)</div>
          </div>
          <p><span style={{ color: '#ffaa44', fontWeight: 600 }}>Exposure</span> builds as you move through busy roads and zones. It decays slowly. Safehouses (blue house icons) reset your exposure to zero.</p>
          <p>You can also win by <span style={{ color: '#ff5050', fontWeight: 600 }}>eliminating the agent</span> — no extraction needed.</p>
        </div>
      ),
    },
    // 12. Hotkeys
    {
      title: 'HOTKEYS',
      content: (
        <div style={text}>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', margin: '10px 0', alignItems: 'center' }}>
            <Key>Enter</Key><span>End your turn</span>
            <Key>Q</Key><span>Toggle zone overlay</span>
            <Key>R</Key><span>Toggle road overlay</span>
            <Key>F</Key><span>Snap to latest agent intel</span>
            <Key>Space</Key><span>Hold to pan the map (drag)</span>
            <Key>Esc</Key><span>Close dialogs / cancel placement</span>
          </div>
          <p style={{ marginTop: '16px', textAlign: 'center', color: theme.colors.accentGreen, fontWeight: 600, fontSize: '13px' }}>Good luck, operative. The city is watching.</p>
        </div>
      ),
    },
  ];
}

// ─── Component ─────────────────────────────────────────

export function TutorialScreen({ onBack, overlay = false }: TutorialScreenProps) {
  const [slideIndex, setSlideIndex] = useState(0);
  const slides = React.useMemo(() => buildSlides(), []);

  const prev = useCallback(() => setSlideIndex(i => Math.max(0, i - 1)), []);
  const next = useCallback(() => setSlideIndex(i => Math.min(slides.length - 1, i + 1)), [slides.length]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onBack(); return; }
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onBack, prev, next]);

  const slide = slides[slideIndex];

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: overlay ? 500 : 0,
      background: overlay ? 'rgba(0, 0, 0, 0.85)' : theme.colors.background,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: theme.fonts.mono,
    }}>
      <div style={{
        width: '100%', maxWidth: '580px', padding: '32px',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', marginBottom: '24px',
        }}>
          <button
            style={{
              background: 'none', border: `1px solid ${theme.colors.border}`,
              color: theme.colors.textSecondary, fontFamily: theme.fonts.mono,
              fontSize: '10px', letterSpacing: '1px', padding: '4px 10px',
              cursor: 'pointer', borderRadius: '2px',
            }}
            onClick={onBack}
          >
            {overlay ? '✕ CLOSE' : '← MENU'}
          </button>
          <div style={{
            fontSize: '10px', letterSpacing: '2px', color: theme.colors.textDim,
          }}>
            {slideIndex + 1} / {slides.length}
          </div>
        </div>

        {/* Slide title */}
        <div style={{
          fontSize: '18px', fontWeight: 700, letterSpacing: '4px',
          color: theme.colors.accentGreen, marginBottom: '4px', textAlign: 'center',
        }}>
          {slide.title}
        </div>

        {/* Slide content */}
        <div style={{
          width: '100%', minHeight: '280px',
          background: 'rgba(16, 22, 31, 0.8)', borderRadius: '8px',
          border: `1px solid ${theme.colors.border}`, padding: '20px 24px',
          marginTop: '12px',
        }}>
          {slide.content}
        </div>

        {/* Navigation */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', marginTop: '20px',
        }}>
          <button
            style={{
              background: slideIndex === 0 ? 'none' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${slideIndex === 0 ? theme.colors.border : 'rgba(255,255,255,0.15)'}`,
              color: slideIndex === 0 ? theme.colors.textDim : theme.colors.textSecondary,
              fontFamily: theme.fonts.mono, fontSize: '11px', fontWeight: 600,
              letterSpacing: '1px', padding: '8px 20px', cursor: slideIndex === 0 ? 'default' : 'pointer',
              borderRadius: '4px', transition: 'all 0.15s', minWidth: '80px',
            }}
            onClick={prev}
            disabled={slideIndex === 0}
          >
            ← PREV
          </button>

          {/* Dot indicators */}
          <div style={{ display: 'flex', gap: '6px' }}>
            {slides.map((_, i) => (
              <div
                key={i}
                onClick={() => setSlideIndex(i)}
                style={{
                  width: i === slideIndex ? '16px' : '6px', height: '6px',
                  borderRadius: '3px', cursor: 'pointer', transition: 'all 0.2s',
                  background: i === slideIndex ? theme.colors.accentGreen
                    : i < slideIndex ? 'rgba(0, 255, 136, 0.3)' : 'rgba(255, 255, 255, 0.15)',
                }}
              />
            ))}
          </div>

          <button
            style={{
              background: slideIndex === slides.length - 1 ? 'none' : 'rgba(0, 255, 136, 0.06)',
              border: `1px solid ${slideIndex === slides.length - 1 ? theme.colors.border : 'rgba(0, 255, 136, 0.4)'}`,
              color: slideIndex === slides.length - 1 ? theme.colors.textDim : theme.colors.accentGreen,
              fontFamily: theme.fonts.mono, fontSize: '11px', fontWeight: 600,
              letterSpacing: '1px', padding: '8px 20px',
              cursor: slideIndex === slides.length - 1 ? 'default' : 'pointer',
              borderRadius: '4px', transition: 'all 0.15s', minWidth: '80px',
            }}
            onClick={next}
            disabled={slideIndex === slides.length - 1}
          >
            NEXT →
          </button>
        </div>
      </div>
    </div>
  );
}
